import { getCyberProspects } from './cyberOffersService';
import type { CyberProspect } from './cyberOffersService';
import { getUsecureFollowups } from './usecureService';
import type { UsecureClientFollowup } from './usecureService';

export type AdvisorDashboardSource = 'usecure' | 'offers' | 'projects' | 'documents';
export type AdvisorDashboardPriority = 'high' | 'medium' | 'low';
export type AdvisorDashboardBucket = 'today' | 'risk' | 'briefing' | 'documents';

export interface AdvisorDashboardAction {
  label: string;
  kind: 'open-usecure' | 'open-offers' | 'open-project' | 'open-document' | 'prepare-briefing' | 'prepare-document';
  href?: string;
}

export interface AdvisorDashboardItem {
  id: string;
  bucket: AdvisorDashboardBucket;
  clientName: string;
  title: string;
  signal: string;
  why: string[];
  nextAction: string;
  source: AdvisorDashboardSource;
  priority: AdvisorDashboardPriority;
  owner?: string | null;
  statusLabel?: string;
  updatedAt?: string | null;
  actions: AdvisorDashboardAction[];
  sourceUrl?: string;
  sourceRecordId?: string | number | null;
  sourceRecordType?: 'usecure_followup' | 'cyber_prospect' | 'project_task' | 'document';
  correctionKind?: 'assign_referent' | 'create_project_task' | 'complete_next_action' | 'validate_source' | 'review_document';
}

export interface AdvisorDashboardData {
  periodMonth: string;
  generatedAt: string;
  items: AdvisorDashboardItem[];
  sourceStatus: Array<{ source: AdvisorDashboardSource; label: string; loaded: boolean; detail: string }>;
}

function getCurrentPeriodMonth() {
  return new Date().toISOString().slice(0, 7);
}

function priorityRank(priority: AdvisorDashboardPriority) {
  if (priority === 'high') return 3;
  if (priority === 'medium') return 2;
  return 1;
}

function uniq(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter(Boolean) as string[]));
}

function normalizeClientKey(value: string) {
  return value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function usecureFollowupUrl(followup: UsecureClientFollowup) {
  const params = new URLSearchParams({ followupId: String(followup.id), client: followup.clientName });
  return `/solutions-affaires/cybersecurite?outil=usecure&${params.toString()}`;
}

function offersProspectUrl(prospect: CyberProspect) {
  return `/solutions-affaires/offres-de-cybersecurite?prospectId=${encodeURIComponent(prospect.id)}`;
}

function getFollowupStatusLabel(status: UsecureClientFollowup['status']) {
  const labels: Record<UsecureClientFollowup['status'], string> = {
    draft: 'À préparer',
    ready: 'Prêt à envoyer',
    sent: 'Envoyé',
    responded: 'Réponse reçue',
    action_created: 'Action créée',
    closed: 'Fermé',
  };
  return labels[status] || status;
}

function buildUsecureItems(followups: UsecureClientFollowup[]): AdvisorDashboardItem[] {
  const items: AdvisorDashboardItem[] = [];
  for (const followup of followups) {
    const metrics = followup.generatedSummary?.metrics || {};
    const score = followup.health?.score ?? 0;
    const owner = followup.servoComparison?.referentCyber || followup.servoComparison?.prime || followup.confirmedProjectLink?.ownerName || null;
    const projectUrl = followup.projectTaskLink?.url || followup.confirmedProjectLink?.url || followup.servoComparison?.projectLink?.url;
    const sourceUrl = usecureFollowupUrl(followup);
    const needsTask = !followup.projectTaskLink && Boolean(followup.confirmedProjectLink || followup.servoComparison?.projectLink?.status === 'confirmed_manual');
    const riskReasons = uniq([
      score < 70 ? `Score uSecure ${score}%` : null,
      metrics.trainingCompletionRate !== undefined && metrics.trainingCompletionRate < 80 ? `Formation complétée à ${metrics.trainingCompletionRate}%` : null,
      metrics.openHighBreaches ? `${metrics.openHighBreaches} exposition(s) uBreach élevée(s)` : null,
      metrics.compromises ? `${metrics.compromises} compromission(s) uPhish` : null,
      followup.clientQuestions ? 'Question client ouverte' : null,
      needsTask ? 'Tâche projet manquante' : null,
    ]);

    if (followup.status === 'draft' || followup.status === 'ready' || needsTask) {
      items.push({
        id: `usecure-today-${followup.id}`,
        bucket: 'today',
        clientName: followup.clientName,
        title: followup.status === 'ready' ? 'Suivi uSecure prêt à envoyer' : 'Suivi uSecure à préparer',
        signal: needsTask ? 'Tâche projet manquante pour le suivi mensuel' : getFollowupStatusLabel(followup.status),
        why: uniq([
          `Statut : ${getFollowupStatusLabel(followup.status)}`,
          needsTask ? 'Lien projet confirmé, mais aucune tâche créée' : null,
          followup.nextAction || followup.generatedSummary?.recommendations?.[0],
        ]),
        nextAction: needsTask ? 'Créer ou ouvrir la tâche projet associée' : followup.status === 'ready' ? 'Envoyer le suivi au client' : 'Réviser le suivi mensuel',
        source: 'usecure',
        priority: needsTask || score < 70 ? 'high' : 'medium',
        owner,
        statusLabel: getFollowupStatusLabel(followup.status),
        updatedAt: followup.sentAt || followup.nextFollowupDate || null,
        sourceUrl,
        sourceRecordId: followup.id,
        sourceRecordType: 'usecure_followup',
        correctionKind: needsTask ? 'create_project_task' : followup.status === 'ready' ? 'validate_source' : 'complete_next_action',
        actions: [
          { label: 'Ouvrir suivi uSecure', kind: 'open-usecure', href: sourceUrl },
          ...(projectUrl ? [{ label: 'Ouvrir projet', kind: 'open-project' as const, href: projectUrl }] : []),
          { label: 'Préparer briefing', kind: 'prepare-briefing' },
        ],
      });
    }

    if (riskReasons.length > 0) {
      items.push({
        id: `usecure-risk-${followup.id}`,
        bucket: 'risk',
        clientName: followup.clientName,
        title: 'Client à surveiller',
        signal: riskReasons[0],
        why: riskReasons,
        nextAction: followup.nextAction || 'Valider le suivi, puis planifier une relance ciblée',
        source: 'usecure',
        priority: score < 70 || Boolean(metrics.openHighBreaches) || Boolean(metrics.compromises) ? 'high' : 'medium',
        owner,
        statusLabel: `${score}%`,
        updatedAt: followup.nextFollowupDate || null,
        sourceUrl,
        sourceRecordId: followup.id,
        sourceRecordType: 'usecure_followup',
        correctionKind: needsTask ? 'create_project_task' : followup.status === 'ready' ? 'validate_source' : 'complete_next_action',
        actions: [
          { label: 'Ouvrir suivi uSecure', kind: 'open-usecure', href: sourceUrl },
          { label: 'Préparer briefing', kind: 'prepare-briefing' },
        ],
      });
    }

    if (followup.status !== 'closed') {
      items.push({
        id: `usecure-briefing-${followup.id}`,
        bucket: 'briefing',
        clientName: followup.clientName,
        title: 'Briefing de suivi uSecure prêt',
        signal: followup.generatedEmailSubject || 'Résumé mensuel disponible',
        why: uniq([...(followup.generatedSummary?.highlights || []).slice(0, 2), ...(followup.generatedSummary?.recommendations || []).slice(0, 1)]),
        nextAction: 'Préparer un court briefing conseiller avant la relance',
        source: 'usecure',
        priority: riskReasons.length > 0 ? 'high' : 'medium',
        owner,
        statusLabel: getFollowupStatusLabel(followup.status),
        updatedAt: followup.nextFollowupDate || null,
        sourceUrl,
        sourceRecordId: followup.id,
        sourceRecordType: 'usecure_followup',
        correctionKind: 'validate_source',
        actions: [
          { label: 'Préparer briefing', kind: 'prepare-briefing' },
          { label: 'Ouvrir suivi uSecure', kind: 'open-usecure', href: sourceUrl },
        ],
      });
    }

    if (followup.status === 'sent' || followup.status === 'responded' || followup.sentEmailHistory?.length) {
      items.push({
        id: `usecure-document-${followup.id}`,
        bucket: 'documents',
        clientName: followup.clientName,
        title: 'Rapport mensuel uSecure à réviser',
        signal: 'Rapport client / PDF disponible depuis le suivi',
        why: uniq([
          followup.sentAt ? 'Suivi déjà envoyé' : null,
          followup.sentEmailHistory?.length ? `${followup.sentEmailHistory.length} envoi(s) historisé(s)` : null,
          'Rapport mensuel prêt à produire ou remettre',
        ]),
        nextAction: 'Ouvrir le suivi, générer/réviser le rapport client',
        source: 'documents',
        priority: 'medium',
        owner,
        statusLabel: 'Rapport',
        updatedAt: followup.sentAt || null,
        sourceUrl,
        sourceRecordId: followup.id,
        sourceRecordType: 'usecure_followup',
        correctionKind: needsTask ? 'create_project_task' : followup.status === 'ready' ? 'validate_source' : 'complete_next_action',
        actions: [
          { label: 'Ouvrir suivi uSecure', kind: 'open-usecure', href: sourceUrl },
          { label: 'Préparer document', kind: 'prepare-document' },
        ],
      });
    }
  }
  return items;
}

function buildOfferItems(prospects: CyberProspect[]): AdvisorDashboardItem[] {
  const items: AdvisorDashboardItem[] = [];
  const activeProspects = prospects.filter((prospect) => !['archived', 'closed', 'deleted'].includes(String(prospect.status || '').toLowerCase()));
  for (const prospect of activeProspects) {
    const proposalCount = prospect.proposalCount || 0;
    const status = String(prospect.status || 'actif');
    const hasNoProposal = proposalCount === 0;
    const sourceUrl = offersProspectUrl(prospect);
    items.push({
      id: `offer-${prospect.id}`,
      bucket: hasNoProposal ? 'today' : 'briefing',
      clientName: prospect.organizationName,
      title: hasNoProposal ? 'Offre cyber à structurer' : 'Offre cyber à suivre',
      signal: hasNoProposal ? 'Aucune proposition active liée' : `${proposalCount} proposition(s) liée(s)`,
      why: uniq([
        `Statut prospect : ${status}`,
        hasNoProposal ? 'Le dossier peut nécessiter les questions de qualification et une proposition' : 'Un briefing de présentation ou de relance peut être préparé',
        prospect.notes || null,
      ]),
      nextAction: hasNoProposal ? 'Ouvrir les offres cyber et compléter le parcours conseiller' : 'Préparer le briefing de présentation ou de relance',
      source: 'offers',
      priority: hasNoProposal ? 'medium' : 'low',
      owner: prospect.ownerUserId ? `Utilisateur ${prospect.ownerUserId}` : null,
      statusLabel: status,
      updatedAt: prospect.updatedAt,
      sourceUrl,
      sourceRecordId: prospect.id,
      sourceRecordType: 'cyber_prospect',
      correctionKind: hasNoProposal ? 'complete_next_action' : 'validate_source',
      actions: [
        { label: hasNoProposal ? 'Ouvrir prospect' : 'Ouvrir offre', kind: 'open-offers', href: sourceUrl },
        { label: 'Préparer briefing', kind: 'prepare-briefing' },
      ],
    });
  }
  return items;
}

export async function getCyberAdvisorDashboardData(periodMonth = getCurrentPeriodMonth()): Promise<AdvisorDashboardData> {
  const sourceStatus: AdvisorDashboardData['sourceStatus'] = [];
  const allItems: AdvisorDashboardItem[] = [];

  const [usecureResult, prospectsResult] = await Promise.allSettled([
    getUsecureFollowups(periodMonth),
    getCyberProspects(),
  ]);

  if (usecureResult.status === 'fulfilled') {
    const followups = usecureResult.value.followups || [];
    allItems.push(...buildUsecureItems(followups));
    sourceStatus.push({ source: 'usecure', label: 'uSecure', loaded: true, detail: `${followups.length} suivi(s) lu(s)` });
  } else {
    sourceStatus.push({ source: 'usecure', label: 'uSecure', loaded: false, detail: usecureResult.reason?.message || 'Source indisponible' });
  }

  if (prospectsResult.status === 'fulfilled') {
    const prospects = prospectsResult.value || [];
    allItems.push(...buildOfferItems(prospects));
    sourceStatus.push({ source: 'offers', label: 'Offres cyber', loaded: true, detail: `${prospects.length} prospect(s) lu(s)` });
  } else {
    sourceStatus.push({ source: 'offers', label: 'Offres cyber', loaded: false, detail: prospectsResult.reason?.message || 'Source indisponible' });
  }

  sourceStatus.push({ source: 'projects', label: 'Projets', loaded: true, detail: 'Couvert via liens projet uSecure pour cette version' });
  sourceStatus.push({ source: 'documents', label: 'Documents', loaded: true, detail: 'Couvert via rapports uSecure et file de documents à produire' });

  const dedupedItems = Array.from(
    new Map(
      allItems
        .sort((a, b) => priorityRank(b.priority) - priorityRank(a.priority) || new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
        .map((item) => [`${normalizeClientKey(item.clientName)}:${item.source}:${item.bucket}:${item.signal}:${item.nextAction}`, item])
    ).values()
  );
  dedupedItems.sort((a, b) => priorityRank(b.priority) - priorityRank(a.priority) || a.clientName.localeCompare(b.clientName));

  return {
    periodMonth,
    generatedAt: new Date().toISOString(),
    items: dedupedItems,
    sourceStatus,
  };
}
